import type { OrderDraft, RegisterResult } from '@jywer/intake';

export interface IntakeRegisterError {
  code: string;
  message: string;
  // server may include zod issues, validateOrder missing-fields, etc.
  details?: unknown;
}

export type IntakeRegisterResponse =
  | { ok: true; result: RegisterResult }
  | { ok: false; status: number; error: IntakeRegisterError };

/**
 * POSTs to the same `/api/intake/register_order` endpoint the Claude
 * skill uses. The server re-runs zod + validateOrder, so this is the
 * authoritative validator — the client-side `orderDraftSchema` only
 * exists for inline UX feedback.
 */
export async function registerOrder(draft: OrderDraft): Promise<IntakeRegisterResponse> {
  const res = await fetch('/api/intake/register_order', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(draft),
  });
  if (res.ok) {
    const result = (await res.json()) as RegisterResult;
    return { ok: true, result };
  }
  const body = await res.json().catch(() => null);
  const err = (body ?? {}) as Partial<IntakeRegisterError>;
  return {
    ok: false,
    status: res.status,
    error: {
      code: err.code ?? 'http_error',
      message: err.message ?? `HTTP ${res.status}`,
      details: err.details,
    },
  };
}

export interface SalesRepOption {
  id: string;
  display_name: string;
  email: string;
  role: string;
}

/**
 * Lists candidate sales reps for the rep dropdown. Hits the existing
 * `/api/admin/users` endpoint (gated by `view:admin.users`) and filters
 * client-side to roles that conceivably attribute orders. Falls back to
 * the orders-derived `_sales-reps` endpoint when the caller lacks
 * `view:admin.users` (e.g. a partner with `edit:admin.orders` only).
 */
export async function fetchAllSalesReps(): Promise<SalesRepOption[]> {
  const usersRes = await fetch('/api/admin/users');
  if (usersRes.ok) {
    const data = (await usersRes.json()) as {
      users: Array<{
        id: string;
        email: string;
        display_name: string;
        role: string;
        active: boolean;
      }>;
    };
    return data.users
      .filter((u) => u.active && (u.role === 'admin' || u.role === 'sales'))
      .map((u) => ({ id: u.id, display_name: u.display_name, email: u.email, role: u.role }))
      .sort((a, b) => a.display_name.localeCompare(b.display_name, 'da-DK'));
  }
  if (usersRes.status === 403) {
    const fallback = await fetch('/api/admin/orders/_sales-reps');
    if (!fallback.ok) return [];
    const data = (await fallback.json()) as { reps: Array<{ id: string; display_name: string }> };
    return data.reps.map((r) => ({
      id: r.id,
      display_name: r.display_name,
      email: '',
      role: 'sales',
    }));
  }
  throw new Error(`Kunne ikke hente sælgere (HTTP ${usersRes.status})`);
}

export interface ClientOption {
  id: string;
  name: string;
  invoice_email: string | null;
  vat_number: string | null;
}

/**
 * Lists existing clients for the autocomplete picker. Reuses the admin
 * clients endpoint (page_size=500 is plenty for JYWER's client base).
 */
export async function fetchClientsList(): Promise<ClientOption[]> {
  const url = new URL('/api/admin/clients', window.location.origin);
  url.searchParams.set('page_size', '500');
  const res = await fetch(url.toString());
  if (!res.ok) {
    if (res.status === 403) return [];
    throw new Error(`Kunne ikke hente kunder (HTTP ${res.status})`);
  }
  const data = (await res.json()) as {
    rows?: Array<{
      id: string;
      name: string;
      invoice_email: string | null;
      vat_number: string | null;
    }>;
  };
  return data.rows ?? [];
}

export interface MeResponse {
  userId: string;
  email: string;
  permissions: readonly string[];
}

export async function fetchMe(): Promise<MeResponse | null> {
  const res = await fetch('/api/me');
  if (!res.ok) return null;
  return (await res.json()) as MeResponse;
}
